import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { IdentityUser, Menu } from '@model/identity';
import { GetIdentityUserUseCase } from '@usecase/identity';
import { OptionItem } from '@view/shared/molecules/option-item/option-item';
import { getIdentityDi } from '@cfg/security/identity-di';
import { CommonModule } from '@angular/common';
import { IdentityOperations } from '@model/identity/operations';
import { RouteService } from '@helpers/routing/router.service';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { SuraUiModule } from '@view/shared/sura-ui.module';
import { NavbarComponent } from '@view/shared/organisms/navbar/navbar.component';
import { IdentityServiceImpl } from '@adapter/security/identity.service';
import { SidebarComponent } from '@view/shared/organisms/sidebar/sidebar.component';
import { ToastComponent } from '@view/shared/molecules/toast/toast.component';
import { ViewExpansionStateService } from '@helpers/state/view-expansion-state.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-layout',
  imports: [
    RouterModule,
    CommonModule,
    SuraUiModule,
    NavbarComponent,
    SidebarComponent,
    ToastComponent
  ],
  providers: [getIdentityDi],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit {
  @ViewChild('sidebar') sidebar?: ElementRef;

  usecase = inject(GetIdentityUserUseCase);
  translator = inject(AppTranslationService);
  router = inject(RouteService);
  ngZone = inject(NgZone);
  identityService = inject(IdentityServiceImpl);
  viewExpansionState = inject(ViewExpansionStateService);

  userMenu = signal<OptionItem[]>([]);
  user = signal<IdentityUser | null>(null);
  seusMenus = signal<Menu[]>([]);

  userForNavbar = computed(() => {
    const currentUser = this.user();
    if (currentUser) {
      return {
        fullName: currentUser.fullName,
        lastAccess: new Date(currentUser.lastAccess)
      };
    }
    return { fullName: 'Usuario', lastAccess: new Date() };
  });

  userMenuState = signal('closed');
  menuItems = signal<OptionItem[]>([]);
  menuState = signal('closed');
  userMenuActive = false;
  sidebarMinimized = false;

  ngOnInit(): void {
    this.identityService.getUserInfo().subscribe((user: IdentityUser) => {
      this.user.set(user);
      this.prepareAppMenu(user);
    });

    this.ngZone.runOutsideAngular(() => {
      const elem = this.sidebar?.nativeElement as HTMLElement;
      if (elem) {
        elem.addEventListener('mouseenter', () => {
          this.onMenuStateChange('opened');
        });
        elem.addEventListener('mouseleave', () => {
          this.onMenuStateChange('closed');
        });
      }
    });
  }

  logout(): void {
    const currentUser = this.user();

    if (currentUser?.urlLogout) {
      this.identityService.clearObserverForLogin();
      this.navigateTo(currentUser.urlLogout);
    } else if (currentUser?.fullUrlLogout) {
      this.identityService.fullLogout(currentUser.fullUrlLogout);
    } else {
      this.identityService.clearObserverForLogin();
      sessionStorage.clear();
      this.navigateTo('/');
    }
  }

  protected navigateTo(url: string): void {
    globalThis.location.href = url;
  }

  onMenuStateChange(event: string) {
    this.menuState.set(event);
  }

  onUserMenuStateChange(event: string) {
    this.userMenuState.set(event);
  }

  onUserMenuItemClick(item: OptionItem) {
    if (item.id === 'logout') {
      // Logout logic handled by logout() method
    } else {
      console.warn('Opción de menú no soportada:', item);
    }
  }

  onMenuItemClicked(item: OptionItem) {
    this.router.navigateTo(item.path);
    this.menuState.set('closed');
  }

  isUserMenuOpen = computed(() => this.userMenuState() === 'opened');
  isMenuOpen = computed(() => this.menuState() === 'opened');

  private prepareAppMenu(user: IdentityUser) {
    const appMenu = IdentityOperations.getMenu(user);
    this.seusMenus.set(appMenu);

    if (!appMenu || appMenu.length === 0) {
      console.warn('No hay menús disponibles para el usuario');
      this.menuItems.set([]);
      this.userMenu.set([]);
      return;
    }

    this.prepareUserMenu(user);
  }

  private prepareUserMenu(user: IdentityUser): void {
    this.userMenu.set([
      {
        id: 'logout',
        path: user.urlLogout,
        title: this.translator.get('security.logout'),
        icon: 'user'
      }
    ]);
  }

  get isExpanded(): boolean {
    return this.viewExpansionState.isExpanded();
  }
}
